import React from 'react';
import {
  FormControl,
  InputLabel,
  Select as NativeSelect,
  FormHelperText,
  SelectChangeEvent
} from '@mui/material';
import clsx from 'clsx';

import { FormMode } from '../../enums';
import { SelectProps } from '../../interfaces';
import { useValidateInput, useFormContext, useGetFieldRules } from '../../hooks';
import { FormContext, FormRefsContext } from '../../contexts';
import { alphabetizeOptions } from '../../utils';
import { TooltipContainer } from '../tooltip-container';

type FieldData = { value: string; text: string };

export function Select({ name, ...props }: SelectProps): JSX.Element {
  const contextRules = useGetFieldRules<SelectProps>(name);
  return <SelectComponent {...{ ...contextRules, ...props }} name={name} />;
}

function SelectComponent({
  sort = true,
  removeBlankOption = false,
  ...props
}: SelectProps): JSX.Element {
  const { formMode } = React.useContext(FormContext);
  const { fieldsRefs } = React.useContext(FormRefsContext);
  const options = React.useMemo(() => {
    const { otherOptions, latestOptions } = Object.entries(props.options ?? {}).reduce(
      (prevValue, currentValue) => {
        const [value, text] = currentValue;
        (props.lastOptions?.includes(value)
          ? prevValue.latestOptions
          : prevValue.otherOptions
        ).push({
          value,
          text
        });
        return prevValue;
      },
      { otherOptions: [] as FieldData[], latestOptions: [] as FieldData[] }
    );

    const sorted = !!sort ? otherOptions.sort(alphabetizeOptions) : otherOptions;
    return sorted.concat(latestOptions);
  }, [props.options, props.lastOptions]);

  const { error, validateInput } = useValidateInput(props);

  const { setValueByName } = useFormContext();

  const setSelected = (value: string): void => {
    const fieldValue = value === '' ? undefined : value;
    setValueByName(props.name, fieldValue);
    if (props.onChange) {
      props.onChange(fieldValue);
    }
    validateInput(fieldValue);
  };

  const handleChange = (event: SelectChangeEvent<string>): void => {
    setSelected(event.target.value);
  };

  React.useEffect(() => {
    if (props.defaultValue != null && props.defaultValue !== '') {
      setSelected(props.defaultValue);
    }
    if (props.defaultValue != null) validateInput(props.defaultValue);
  }, []);

  return (
    <TooltipContainer tooltip={props.tooltip}>
      <FormControl
        id={props.id}
        className={clsx(
          {
            'form-field': true,
            'required-submit': props.requiredToSubmit
          },
          props.className
        )}
        variant='outlined'
        required={props.required}
        error={error != null}
        fullWidth={props.fullWidth}
        size={props.size ?? 'medium'}
        ref={(el: HTMLDivElement) => {
          if (fieldsRefs && props?.name) {
            fieldsRefs.current[props.name] = el;
          }
        }}
      >
        <InputLabel sx={{ maxWidth: 'calc(100% - 36px)' }}>{props.label}</InputLabel>
        <NativeSelect
          native
          inputProps={{
            name: props.name,
            defaultValue: props.defaultValue === null ? undefined : props.defaultValue
          }}
          onChange={handleChange}
          disabled={props.disabled || formMode === FormMode.ReadOnly}
          label={props.label}
          fullWidth
          value={props.value === null ? undefined : props.value}
        >
          {!removeBlankOption && <option aria-label='None' value='' />}
          {options.map(({ value, text }) => (
            <option key={value} value={value}>
              {text}
            </option>
          ))}
        </NativeSelect>
        <FormHelperText>{error ? error : props.helperText}</FormHelperText>
      </FormControl>
    </TooltipContainer>
  );
}
